import { useEffect } from 'react';
import type { DocRankerFilter } from '@packages/types';
import { Select, Slider, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { DocumentFilterCondition } from '../../types';
import { CANCER_TYPE_OPTIONS, DOC_RANKER_SCORE_MARKS, DEFAULT_MIN_SCORE } from '../../constants';

interface DocRankerConditionInputProps {
    condition: DocumentFilterCondition;
    groupId: string;
    updateCondition: (groupId: string, conditionId: string, updates: Partial<DocumentFilterCondition>) => void;
}

/**
 * DocRanker condition input for document relevance ranking.
 * Renders as a filter condition row with minScore slider and cancerType select.
 */
export const DocRankerConditionInput: React.FC<DocRankerConditionInputProps> = ({
    condition,
    groupId,
    updateCondition,
}) => {
    // Value is stored as DocRankerFilter object
    const docRankerValue = (condition.value as DocRankerFilter) || { enabled: true, minScore: DEFAULT_MIN_SCORE };

    const handleMinScoreChange = (minScore: number) => {
        updateCondition(groupId, condition.id, {
            value: {
                ...docRankerValue,
                enabled: true,
                minScore,
            },
        });
    };

    const handleCancerTypeChange = (cancerType: string | undefined) => {
        updateCondition(groupId, condition.id, {
            value: {
                ...docRankerValue,
                enabled: true,
                cancerType: cancerType as DocRankerFilter['cancerType'],
            },
        });
    };

    // Auto-enable when this condition is first selected
    useEffect(() => {
        if (!condition.value) {
            updateCondition(groupId, condition.id, {
                value: { enabled: true, minScore: DEFAULT_MIN_SCORE },
            });
        }
    }, [condition.value, condition.id, groupId, updateCondition]);

    return (
        <div className="flex w-full flex-col gap-3 rounded border border-gray-100 bg-gray-50 p-3">
            <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">
                    Scores documents by cancer-relevant content (pathology, diagnoses, staging)
                </span>
                <Tooltip
                    title={
                        <div className="text-xs">
                            <p className="mb-1 font-medium">Document Ranking</p>
                            <p>
                                Scores documents based on content analysis for cancer-relevant signals: pathology
                                reports, diagnoses, staging, ICD-O codes, and more.
                            </p>
                            <p className="mt-1">Documents below the minimum score will be excluded.</p>
                        </div>
                    }
                >
                    <InfoCircleOutlined className="cursor-help text-gray-400" />
                </Tooltip>
            </div>

            {/* Minimum Score Slider */}
            <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                    <label className="text-xs text-gray-600">Minimum Relevance Score</label>
                    <span className="text-xs font-medium text-[#0098DA]">
                        {docRankerValue.minScore ?? DEFAULT_MIN_SCORE}%
                    </span>
                </div>
                <Slider
                    min={0}
                    max={100}
                    step={5}
                    value={docRankerValue.minScore ?? DEFAULT_MIN_SCORE}
                    onChange={handleMinScoreChange}
                    marks={DOC_RANKER_SCORE_MARKS}
                    tooltip={{ formatter: (val?: number) => `${val}%` }}
                    className="[&_.ant-slider-mark-text]:!text-[10px]"
                />
                <div className="flex justify-between text-[10px] text-gray-400">
                    <span>Include more documents</span>
                    <span>Higher relevance only</span>
                </div>
            </div>

            {/* Cancer Type Filter */}
            <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-600">Cancer Type Boost (Optional)</label>
                <Select
                    allowClear
                    placeholder="Any cancer type"
                    options={[...CANCER_TYPE_OPTIONS]}
                    value={docRankerValue.cancerType}
                    onChange={handleCancerTypeChange}
                    size="small"
                    className="w-full [&_.ant-select-selection-item]:!text-[12px] [&_.ant-select-selection-placeholder]:!text-[12px]"
                />
                <p className="text-[10px] text-gray-400">Boosts documents mentioning the selected cancer type.</p>
            </div>
        </div>
    );
};
